import type { ElementType } from "react";

import {
  AnalyticsIcon,
  DashboardsIcon,
  EyeIcon,
  IdeaIcon,
  LaunchAdIcon,
  StarBadgeIcon,
  TimeIcon,
  TrendsIcon,
} from "@hightouch/htui";

import { useHasProductTabs } from "src/components/layout/nav/sections/hooks/projects";
import { useIsAdStudioDemoEnabled } from "src/pages/ad-studio-demo/use-is-ad-studio-demo-enabled";
import { useOpportunitiesEnabled } from "src/pages/ad-studio/components/opportunities-feature-route";
import { useLaunchesEnabled } from "src/pages/ad-studio/insights/launches/launches-feature-route";

export const INSIGHTS_BASE = "/ad-studio/insights";

type InsightsNavItem = {
  label: string;
  /** Route segment under `/ad-studio/insights`. */
  path: string;
  icon: ElementType;
};

const HOME_NAV_ITEM: InsightsNavItem = {
  label: "Home",
  path: "home",
  icon: DashboardsIcon,
};

const NAV_ITEMS: InsightsNavItem[] = [
  { label: "Fatigue", path: "fatigue", icon: TimeIcon },
  { label: "Inspiration", path: "inspiration", icon: EyeIcon },
  { label: "Signals", path: "signals", icon: TrendsIcon },
];

const LAUNCHES_NAV_ITEM: InsightsNavItem = {
  label: "Launches",
  path: "launches",
  icon: LaunchAdIcon,
};

// The AMP nav has no Ads section, so AMP users find the ideas feed here.
const IDEAS_NAV_ITEM: InsightsNavItem = {
  label: "Ideas",
  path: "ideas",
  icon: IdeaIcon,
};

const OPPORTUNITIES_NAV_ITEM: InsightsNavItem = {
  label: "Opportunities",
  path: "opportunities",
  icon: StarBadgeIcon,
};

const PERFORMANCE_NAV_ITEM: InsightsNavItem = {
  label: "Performance",
  path: "performance",
  icon: AnalyticsIcon,
};

/** Every page the section can list, whatever the viewer's gates. */
export const ALL_INSIGHTS_NAV_ITEMS = [
  HOME_NAV_ITEM,
  LAUNCHES_NAV_ITEM,
  PERFORMANCE_NAV_ITEM,
  ...NAV_ITEMS,
  IDEAS_NAV_ITEM,
  OPPORTUNITIES_NAV_ITEM,
];

/** The section's pages in rail order; the AMP nav lists the same ones. */
export function useInsightsNavItems(): InsightsNavItem[] {
  const isDemoEnabled = useIsAdStudioDemoEnabled();
  const isOpportunitiesEnabled = useOpportunitiesEnabled();
  const isLaunchesEnabled = useLaunchesEnabled();
  const hasProductTabs = useHasProductTabs();
  return [
    ...(isDemoEnabled ? [HOME_NAV_ITEM] : []),
    ...(isLaunchesEnabled ? [LAUNCHES_NAV_ITEM] : []),
    ...NAV_ITEMS,
    ...(hasProductTabs ? [IDEAS_NAV_ITEM] : []),
    ...(isOpportunitiesEnabled ? [OPPORTUNITIES_NAV_ITEM] : []),
  ];
}

/** With no ad platform connected there are no saved views, so Performance, where the section lands, takes its row in the pages' order instead. */
export const withPerformanceRow = (
  items: InsightsNavItem[],
  hasNoConnectedChannels: boolean,
): InsightsNavItem[] =>
  hasNoConnectedChannels
    ? ALL_INSIGHTS_NAV_ITEMS.filter(
        (item) => items.includes(item) || item === PERFORMANCE_NAV_ITEM,
      )
    : items;
